'use client'

import { useSearchParams } from 'next/navigation'
import { FormEvent, useMemo, useState } from 'react'

import { AddressAutocompleteInput } from '@/components/campaigns/address-autocomplete-input'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { InputGroup } from '@/components/ui/input-group'
import { Input } from '@/components/ui/input'

type CampaignSignupFlowProps = {
  rulesHref: string
  campaignName: string
  campaignId: string
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
type UtmKey = (typeof UTM_KEYS)[number]

type Step = 'form' | 'select' | 'registered' | 'not-found'

type FormState = {
  firstName: string
  lastName: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  lat: number | null
  lng: number | null
  email: string
  phone: string
  smsOptIn: boolean
  emailOptIn: boolean
}

const INITIAL_FORM_STATE: FormState = {
  firstName: '',
  lastName: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  lat: null,
  lng: null,
  email: '',
  phone: '',
  smsOptIn: false,
  emailOptIn: false,
}

function CampaignProgressBar({ filledSegments }: { filledSegments: number }) {
  return (
    <div className="flex h-5 divide-x divide-border overflow-hidden rounded-full border border-border">
      {Array.from({ length: 4 }).map((_, index) => {
        const filled = index < filledSegments
        return (
          <div
            key={index}
            className={`min-w-0 flex-1 ${filled ? 'bg-theme-button-primary-background' : 'bg-transparent'}`}
          />
        )
      })}
    </div>
  )
}

function VoterRecordCard({ formState, selectable }: { formState: FormState; selectable?: boolean }) {
  const fullName = `${formState.firstName} ${formState.lastName}`.trim() || 'Voter Name'
  const locationLine = [formState.city, [formState.state, formState.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0 space-y-1">
          <CardTitle>{fullName}</CardTitle>
          <CardDescription>{formState.address || 'Address unavailable'}</CardDescription>
          {formState.address2 ? <CardDescription>{formState.address2}</CardDescription> : null}
          <CardDescription>{locationLine || 'City, ST ZIP'}</CardDescription>
        </div>
        {selectable ? (
          <CardAction>
            <span aria-hidden className="text-foreground">
              →
            </span>
          </CardAction>
        ) : null}
      </CardHeader>
    </Card>
  )
}

export function CampaignSignupFlow({ rulesHref, campaignName, campaignId }: CampaignSignupFlowProps) {
  const searchParams = useSearchParams()
  const [step, setStep] = useState<Step>('form')
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const attribution = useMemo(() => {
    const utm: Partial<Record<UtmKey, string>> = {}
    for (const key of UTM_KEYS) {
      const value = searchParams.get(key)?.trim()
      if (value) utm[key] = value
    }
    const ref = searchParams.get('ref')?.trim() || undefined
    return { utm, ref }
  }, [searchParams])

  const filledSegments = useMemo(() => {
    if (step === 'form') return 1
    if (step === 'registered') return 4
    return 2
  }, [step])

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return
    setSubmitError(null)
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/campaign-entries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign: campaignId,
          firstName: formState.firstName,
          lastName: formState.lastName,
          email: formState.email,
          phone: formState.phone,
          address: formState.address,
          address2: formState.address2 || undefined,
          city: formState.city || undefined,
          state: formState.state || undefined,
          zip: formState.zip || undefined,
          lat: formState.lat,
          lng: formState.lng,
          smsOptIn: formState.smsOptIn,
          emailOptIn: formState.emailOptIn,
          ref: attribution.ref,
          ...attribution.utm,
        }),
      })

      if (!response.ok) {
        let message = 'Unable to save your entry. Please try again.'
        try {
          const payload = (await response.json()) as { message?: string }
          if (payload?.message) message = payload.message
        } catch {
          // keep default
        }
        throw new Error(message)
      }

      setStep('select')
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Unable to save your entry. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const updateForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="space-y-8">
      {step === 'form' ? <h1 className="header-size-2">{campaignName}</h1> : null}
      <CampaignProgressBar filledSegments={filledSegments} />

      {step === 'form' ? (
        <form className="space-y-4" onSubmit={onSubmit}>
          <InputGroup>
            <label htmlFor="campaign-signup-first-name">First name</label>
            <Input
              id="campaign-signup-first-name"
              required
              autoComplete="given-name"
              placeholder="First name"
              value={formState.firstName}
              onChange={(event) => updateForm('firstName', event.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <label htmlFor="campaign-signup-last-name">Last name</label>
            <Input
              id="campaign-signup-last-name"
              required
              autoComplete="family-name"
              placeholder="Last name"
              value={formState.lastName}
              onChange={(event) => updateForm('lastName', event.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <label htmlFor="campaign-signup-address">Address</label>
            <AddressAutocompleteInput
              id="campaign-signup-address"
              required
              value={formState.address}
              onValueChange={(value) => {
                updateForm('address', value)
                updateForm('city', '')
                updateForm('state', '')
                updateForm('zip', '')
                updateForm('lat', null)
                updateForm('lng', null)
              }}
              onPlaceSelected={(place) => {
                updateForm('address', place.address)
                updateForm('city', place.city)
                updateForm('state', place.state)
                updateForm('zip', place.zip)
                updateForm('lat', place.lat)
                updateForm('lng', place.lng)
              }}
            />
          </InputGroup>
          <InputGroup>
            <label htmlFor="campaign-signup-address-2">Address 2</label>
            <Input
              id="campaign-signup-address-2"
              autoComplete="address-line2"
              placeholder="Apartment, suite, etc. (optional)"
              value={formState.address2}
              onChange={(event) => updateForm('address2', event.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <label htmlFor="campaign-signup-email">Email</label>
            <Input
              id="campaign-signup-email"
              required
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={formState.email}
              onChange={(event) => updateForm('email', event.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <label htmlFor="campaign-signup-phone">Phone</label>
            <Input
              id="campaign-signup-phone"
              required
              type="tel"
              autoComplete="tel"
              placeholder="Phone"
              value={formState.phone}
              onChange={(event) => updateForm('phone', event.target.value)}
            />
          </InputGroup>

          <div className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={formState.smsOptIn}
              onChange={(event) => updateForm('smsOptIn', event.target.checked)}
              className="mt-1 size-4 shrink-0"
              style={{ accentColor: 'var(--button-primary-background)' }}
              aria-label="Opt in to election alert text messages from HeadCount"
            />
            <span>
              Sign me up to receive election alert text messages from HeadCount. 4 msgs/month and
              data rates may apply. Text HELP for more information. Text STOP to stop receiving
              messages.
            </span>
          </div>

          <div className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={formState.emailOptIn}
              onChange={(event) => updateForm('emailOptIn', event.target.checked)}
              className="mt-1 size-4 shrink-0"
              style={{ accentColor: 'var(--button-primary-background)' }}
              aria-label="Opt in to election alert emails from HeadCount"
            />
            <span>Sign me up to receive election alert emails from HeadCount.</span>
          </div>

          <p className="text-sm leading-relaxed">
            By clicking submit, you confirm you&apos;ve read and agree to the Official Rules
            (located at HeadCount.org/goodtovote-rules) and Privacy Policy (located at
            HeadCount.org/privacy-policy), and you agree to receive email communications from
            HeadCount. You can unsubscribe at any time. You understand that HeadCount has not
            offered to provide and will not provide any benefit to you in exchange for any civic
            action you take, including checking your voter registration status, registering to
            vote, or having an active voter registration. You understand that no civic action is
            required for entry into the sweepstakes or prize drawing. Void in California, Oregon
            and Mississippi.
          </p>

          <a href={rulesHref} target="_blank" className="block text-sm underline">
            Official Rules
          </a>

          {submitError ? (
            <p
              className="text-sm"
              role="alert"
              style={{ color: 'var(--color-theme-button-destructive-background)' }}
            >
              {submitError}
            </p>
          ) : null}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </form>
      ) : null}

      {step === 'select' ? (
        <section className="space-y-6">
          <h2>Select Your Voter Record</h2>
          <p>
            We found 1 matching results. Select the one that matches where you are or have been
            registered. We&apos;ll help you update your voter registration if needed.
          </p>
          <button type="button" className="block w-full text-left" onClick={() => setStep('registered')}>
            <VoterRecordCard formState={formState} selectable />
          </button>
          <Button type="button" className="w-full" onClick={() => setStep('not-found')}>
            None of These are Me
          </Button>
          <button type="button" className="underline" onClick={() => setStep('form')}>
            Back
          </button>
        </section>
      ) : null}

      {step === 'registered' ? (
        <section className="flex flex-col gap-8">
          <h2>You&apos;re Currently Registered to Vote Here</h2>
          <Card>
            <CardHeader>
              <div className="min-w-0 space-y-1">
                <CardTitle>{`${formState.firstName} ${formState.lastName}`.trim() || 'Voter Name'}</CardTitle>
                <CardDescription>{formState.address || 'Address unavailable'}</CardDescription>
                {formState.address2 ? <CardDescription>{formState.address2}</CardDescription> : null}
                <CardDescription>
                  {[formState.city, [formState.state, formState.zip].filter(Boolean).join(' ')]
                    .filter(Boolean)
                    .join(', ') || 'City, ST ZIP'}
                </CardDescription>
              </div>
              <CardAction>
                <span className="rounded-full border border-border bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                  Registered
                </span>
              </CardAction>
            </CardHeader>
          </Card>

          <div className="flex flex-col gap-4">
            <Button type="button" className="w-full">Vote by Mail</Button>
            <Button type="button" className="w-full">Vote in Person</Button>
            <Button type="button" className="w-full">Update Address</Button>
          </div>
        </section>
      ) : null}

      {step === 'not-found' ? (
        <section className="space-y-5">
          <Card>
            <CardContent className="space-y-4 p-4 md:p-6">
              <h2>No Results Found</h2>
              <p>
                We couldn&apos;t find you based on the information you provided. Here are a couple
                possible reasons why:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>You aren&apos;t registered to vote</li>
                <li>Different first or last name spelling</li>
                <li>Old or incorrect address</li>
                <li>You recently registered to vote</li>
              </ul>
              <div className="flex flex-col gap-3 md:flex-row">
                <Button type="button" className="w-full">Register to Vote</Button>
                <Button type="button" className="w-full" onClick={() => setStep('form')}>
                  Search again
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      ) : null}
    </div>
  )
}
